import { THEME } from '@/constants/Theme';
import '@/utils/polyfills';
import '../global.css';

import { GestureHandlerRootView } from '@/components/gesture-handler';
import { AuthProvider } from '@/contexts/AuthContext';
import { useColorScheme } from '@/hooks/use-color-scheme';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { type Theme, ThemeProvider } from '@react-navigation/native';
import { PortalHost } from '@rn-primitives/portal';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import { Stack, useNavigationContainerRef } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import 'react-native-reanimated';
import { SafeAreaProvider } from 'react-native-safe-area-context';

// Construct a new integration instance. This is needed to communicate between the integration and React
const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true
});

Sentry.init({
  enabled: !__DEV__,
  dsn: 'https://9402652a895c00f16da356b07ff93e72@o4508662256173056.ingest.us.sentry.io/4508662258794496',
  debug: false, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
  tracesSampleRate: 0.1, // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing. Adjusting this value in production.
  integrations: [
    // Pass integration
    navigationIntegration
  ],
  enableNativeFramesTracking: true // Tracks slow and frozen frames in the application
});

const FONTS: Theme['fonts'] = {
  regular: {
    fontFamily: 'Jost',
    fontWeight: '400'
  },
  medium: {
    fontFamily: 'Jost',
    fontWeight: '500'
  },
  bold: {
    fontFamily: 'Jost',
    fontWeight: '700'
  },
  heavy: {
    fontFamily: 'Jost',
    fontWeight: '800'
  }
};

const LIGHT_THEME: Theme = {
  dark: false,
  colors: THEME.light,
  fonts: FONTS
};

const DARK_THEME: Theme = {
  dark: true,
  colors: THEME.dark,
  fonts: FONTS
};

export { ErrorBoundary } from 'expo-router';

// export const unstable_settings = {
//   initialRouteName: '(drawer)'
// };

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

const JOST_FONT = require('../assets/fonts/Jost.ttf');

function RootLayout() {
  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  const [loaded] = useFonts({
    Jost: JOST_FONT
  });

  const { colorScheme, setColorScheme, isDarkColorScheme } = useColorScheme();
  const [isColorSchemeLoaded, setIsColorSchemeLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const theme = await AsyncStorage.getItem('theme');
      if (Platform.OS === 'web') {
        // Adds the background color to the html element to prevent white background on overscroll.
        document.documentElement.classList.add('bg-background');
      }
      if (!theme) {
        AsyncStorage.setItem('theme', colorScheme);
        setIsColorSchemeLoaded(true);
        return;
      }
      const colorTheme = theme === 'dark' ? 'dark' : 'light';
      if (colorTheme !== colorScheme) {
        setColorScheme(colorTheme);

        setIsColorSchemeLoaded(true);
        return;
      }
      setIsColorSchemeLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (loaded && isColorSchemeLoaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded, isColorSchemeLoaded]);

  if (!loaded || !isColorSchemeLoaded) {
    return null;
  }

  return (
    <SafeAreaProvider>
      <GestureHandlerRootView style={styles.container}>
        <ThemeProvider value={isDarkColorScheme ? DARK_THEME : LIGHT_THEME}>
          <AuthProvider>
            <Stack initialRouteName="app">
              <Stack.Screen
                name="(drawer)"
                options={{
                  headerShown: false,
                  headerBackButtonDisplayMode: 'minimal'
                }}
              />
              {/* https://github.com/expo/router/issues/428#issuecomment-2395518024 */}
              <Stack.Screen name="index" redirect />
              <Stack.Screen name="app" options={{ headerShown: false }} />
              <Stack.Screen name="auth" options={{ headerShown: false }} />
              <Stack.Screen
                name="persona-import"
                options={{
                  headerShown: false
                }}
              />
              <Stack.Screen
                name="persona-archive"
                options={{
                  headerShown: false,
                  presentation: 'modal',
                  gestureEnabled: false // prevent accidental dismissal
                }}
              />
              <Stack.Screen
                name="upgrade"
                options={{
                  title: 'Upgrade Now',
                  headerBackTitle: 'Back'
                }}
              />
              <Stack.Screen
                name="+not-found"
                options={{ headerShown: false }}
              />
            </Stack>
            <PortalHost />
          </AuthProvider>
          <SystemBars style="light" />
        </ThemeProvider>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
}

export default Sentry.wrap(RootLayout);

const styles = StyleSheet.create({
  container: {
    flex: 1
  }
});
